import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import Loader from '__components/Kizen/Loader';
import { useToast } from '__components/ToastProvider';
import { getOriginalError } from '__services/AxiosService';
import { getErrorMessage } from '__hooks/useErrors';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { mcpServerQuery, mcpToolsQuery } from 'query/mcp';
import MCPServerService from 'services/MCPServerService';
import { SettingsContentCard } from '__components/Card/Card';
import { Typography } from '@kizen/kds/Typography';
import { Icon } from '@kizen/kds/Icon';
import { TextEllipsisWithTooltip } from '__components/Kizen/Table';
import LongTextInlineBase from '__components/Inputs/inline/LongText';
import {
  CollapsibleGroupedTable,
  type CollapsibleGroupedTableColumn,
  type CollapsibleGroupedTableGroup,
} from 'ts-components/CollapsibleGroupedTable';
import { useSetSaveAction } from '../ToolbarContext';
import { useMCPServerDetailPage } from '../context';
import type { MCPTool } from '@kizen/api/mcp';
import type { ComponentType } from 'react';

const LongTextInline = LongTextInlineBase as unknown as ComponentType<{
  value: string;
  readOnly: boolean;
  fullTooltip?: boolean;
  keyListenerId: string;
  'data-qa-id'?: string;
}>;

const StyledSettingsContentCard = styled(SettingsContentCard)`
  margin: 15px auto 20px;
`;

const StyledSystemManagedNotice = styled(Typography)`
  display: block;
  margin-bottom: 15px;
`;

const StyledReadonlyIconCell = styled.div`
  display: flex;
  justify-content: flex-start;
  padding-left: 15px;
`;

const isReadOnlyTool = (tool: MCPTool) =>
  Boolean((tool.annotations as Record<string, unknown> | null)?.readOnlyHint);

export const MCPServerTools = () => {
  const { t } = useTranslation();
  const { mcpServerId } = useParams<{ mcpServerId: string }>();
  const [showToast, toastVariant] = useToast();
  const queryClient = useQueryClient();
  const setSaveAction = useSetSaveAction();

  const { canWrite, isSystemManaged } = useMCPServerDetailPage();
  const disabled = !canWrite || isSystemManaged;

  const serverQueryOptions = useMemo(
    () =>
      mcpServerQuery(mcpServerId, {
        placeholderData: keepPreviousData,
        staleTime: 0,
        enabled: !!mcpServerId,
      }),
    [mcpServerId]
  );

  const toolsQueryOptions = useMemo(() => mcpToolsQuery(), []);

  const serverQuery = useQuery(serverQueryOptions);
  const { data: server, isLoading: isServerLoading } = serverQuery;
  const { data: toolCatalog, isLoading: isToolsLoading } =
    useQuery(toolsQueryOptions);

  const groups = useMemo<CollapsibleGroupedTableGroup<MCPTool>[]>(
    () =>
      (toolCatalog?.groups ?? []).map((group) => ({
        key: group.key,
        label: group.label,
        items: group.tools,
      })),
    [toolCatalog]
  );

  const [selectedToolNames, setSelectedToolNames] = useState<Set<string>>(
    new Set()
  );
  const prevServerId = useRef<string | null>(null);
  if (server && prevServerId.current !== server.id) {
    setSelectedToolNames(new Set(server.tool_names ?? []));
    prevServerId.current = server.id;
  }

  const columns = useMemo<CollapsibleGroupedTableColumn<MCPTool>[]>(
    () => [
      {
        id: 'readonly',
        label: t('Readonly'),
        width: '100px',
        renderItem: (tool) =>
          isReadOnlyTool(tool) ? (
            <StyledReadonlyIconCell>
              <Icon
                icon="custom-circle-check"
                size="xl"
                color="border-active"
              />
            </StyledReadonlyIconCell>
          ) : null,
      },
      {
        id: 'description',
        label: t('Tool Description (Visible to Agents)'),
        renderItem: (tool) => (
          <LongTextInline
            value={tool.description ?? ''}
            readOnly
            fullTooltip
            keyListenerId={`mcp-tool-description-${tool.name}`}
            data-qa-id={`mcp-tool-description-${tool.name}`}
          />
        ),
      },
    ],
    [t]
  );

  const { mutate: saveTools, isPending: isSaving } = useMutation({
    mutationFn: (toolNames: string[]) =>
      MCPServerService.updateMCPServer(mcpServerId, { tool_names: toolNames }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serverQueryOptions.queryKey });
      showToast({
        message: t('Tool selection saved successfully'),
        variant: toastVariant.SUCCESS,
      });
    },
    onError: (error) => {
      showToast({
        message:
          getErrorMessage(getOriginalError(error)) ||
          t('Failed to save tool selection'),
        variant: toastVariant.FAILURE,
      });
    },
  });

  const isDirty = useMemo(() => {
    if (!server) return false;
    const original = new Set(server.tool_names ?? []);
    if (original.size !== selectedToolNames.size) return true;
    for (const name of selectedToolNames) {
      if (!original.has(name)) return true;
    }
    return false;
  }, [server, selectedToolNames]);

  const handleSaveRef = useRef(() => saveTools(Array.from(selectedToolNames)));
  handleSaveRef.current = () => saveTools(Array.from(selectedToolNames));

  useEffect(() => {
    if (disabled) {
      setSaveAction(null);
      return () => setSaveAction(null);
    }
    setSaveAction({
      onSave: () => handleSaveRef.current(),
      disabled: !isDirty || isSaving,
    });
    return () => setSaveAction(null);
  }, [disabled, isDirty, isSaving, setSaveAction]);

  return (
    <Loader loading={isServerLoading || isToolsLoading}>
      <StyledSettingsContentCard>
        {isSystemManaged ? (
          <StyledSystemManagedNotice variant="placeholder">
            {t(
              'This system-managed server always exposes the built-in read-only tool set and cannot be edited.'
            )}
          </StyledSystemManagedNotice>
        ) : null}
        <CollapsibleGroupedTable
          groups={groups}
          columns={columns}
          nameColumnLabel={t('Tool Name')}
          getItemId={(tool) => tool.name}
          renderItemLabel={(tool) => (
            <TextEllipsisWithTooltip>
              {tool.display_name}
            </TextEllipsisWithTooltip>
          )}
          selectedIds={selectedToolNames}
          onChangeSelectedIds={setSelectedToolNames}
          disabled={disabled}
        />
      </StyledSettingsContentCard>
    </Loader>
  );
};
